import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, X, Sparkles, TrendingUp, Zap, AlertTriangle, Lightbulb,
} from "lucide-react";
import { CAT_LABELS } from "@/lib/categoryLabels";


export function buildInsights({ stats, recent, leaks, alias }) {
  const out = [];
  if (leaks && leaks.length) {
    const total = leaks.reduce((s, l) => s + (Number(l.monthly_estimate || l.amount || 0)), 0);
    out.push({
      id: "leak",
      tone: "warn",
      icon: AlertTriangle,
      title: `${leaks.length} subscription${leaks.length > 1 ? "s" : ""} detected`,
      body: `Potential $${total.toFixed(2)}/mo leak. Review recurring charges.`,
      cta: { label: "Inspect", to: "/receipts" },
    });
  }
  const missingTax = (recent || []).filter((r) => !r.tax || Number(r.tax) === 0).length;
  if (missingTax >= 2) {
    out.push({
      id: "tax",
      tone: "info",
      icon: Lightbulb,
      title: `${missingTax} receipts missing tax field`,
      body: "Add tax data to maximize your deductions at year-end.",
      cta: { label: "Fix now", to: "/receipts" },
    });
  }
  if (stats) {
    const deductible = Number(stats.deductible || 0);
    const total = Number(stats.total_spent || 0);
    if (deductible > 0 && total > 0) {
      const pct = Math.round((deductible / total) * 100);
      out.push({
        id: "deduct",
        tone: "good",
        icon: Zap,
        title: `${pct}% of spend is tax-deductible`,
        body: `$${deductible.toFixed(2)} flagged for write-off. Export before Q1.`,
        cta: { label: "Export CSV", to: "/receipts" },
      });
    }
    const topCat = Object.entries(stats.by_category || {}).sort((a, b) => b[1] - a[1])[0];
    if (topCat && topCat[1] > 0) {
      out.push({
        id: "topcat",
        tone: "info",
        icon: Sparkles,
        title: `Top category: ${CAT_LABELS[topCat[0]] || topCat[0]}`,
        body: `$${Number(topCat[1]).toFixed(2)} spent. Set a budget cap?`,
        cta: { label: "View", to: "/receipts" },
      });
    }
  }
  if (out.length === 0 && alias) {
    out.push({
      id: "onboard",
      tone: "info",
      icon: Lightbulb,
      title: "Forward your first digital receipt",
      body: `Send invoices to ${alias} and we auto-file them.`,
      cta: { label: "Learn how", to: "/upload" },
    });
  }
  return out.slice(0, 4);
}

export function PredictiveInsightsTray({ stats, recent, leaks, alias, onCta }) {
  const [dismissed, setDismissed] = useState({});
  const insights = buildInsights({ stats, recent, leaks, alias }).filter((i) => !dismissed[i.id]);
  if (insights.length === 0) return null;
  const toneMap = {
    warn: "border-amber-400/30 bg-amber-400/5 text-amber-300",
    info: "border-white/10 bg-white/5 text-slate-200",
    good: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300",
  };
  return (
    <div className="mb-8" data-testid="predictive-insights-tray">
      <div className="flex items-center justify-between mb-3">
        <div className="text-emerald-400 font-mono text-[10px] uppercase tracking-[0.3em]">
          Predictive Insights
        </div>
        <div className="text-slate-500 text-[10px] font-mono uppercase tracking-widest">
          {insights.length} signal{insights.length > 1 ? "s" : ""}
        </div>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
        <AnimatePresence initial={false}>
          {insights.map((ins, idx) => {
            const Icon = ins.icon;
            return (
              <motion.div
                key={ins.id}
                data-testid={`insight-${ins.id}`}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.45, delay: idx * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`relative glass rounded-2xl p-4 border ${toneMap[ins.tone]} overflow-hidden`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-black/30 border border-white/10 flex items-center justify-center shrink-0">
                    <Icon size={15} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-sm font-semibold tracking-tight truncate">
                      {ins.title}
                    </div>
                    <div className="text-slate-400 text-xs mt-1 leading-relaxed">
                      {ins.body}
                    </div>
                    {ins.cta && (
                      <button
                        onClick={() => onCta?.(ins.cta.to)}
                        data-testid={`insight-${ins.id}-cta`}
                        className="mt-3 text-xs font-mono text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1"
                      >
                        {ins.cta.label} <ArrowRight size={12} />
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => setDismissed((d) => ({ ...d, [ins.id]: true }))}
                    data-testid={`insight-${ins.id}-dismiss`}
                    className="text-slate-500 hover:text-white p-1 -m-1"
                    aria-label="Dismiss insight"
                  >
                    <X size={13} />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}

